'use client';

import { Button } from '@common/components';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { CustomLink } from '@/app/_components/CustomLink';
import { isDonationBusinessEnded } from '@/app/donation/_utils/isDonationBusinessEnded';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useAutoplayOnInView } from '@/hooks/useAutoplayOnInView';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSelectedSnapIndex } from '@/hooks/useSelectedSnapIndex';

import type { HometownsBusinessItem } from './HometownsBusinessSectionServer';

interface HometownsBusinessSectionProps {
  className?: string;
  list: HometownsBusinessItem[];
}

export const HometownsBusinessSection = ({
  className,
  list,
}: HometownsBusinessSectionProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const [autoplayPlugin] = useState(() =>
    Autoplay({ delay: 3000, stopOnInteraction: false }),
  );
  const { ref: inViewRef, inView } = useInView({ threshold: 0.3 });

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      containScroll: false,
    },
    [autoplayPlugin],
  );
  const isSwiperReady = Boolean(emblaApi);
  const activeIndex = useSelectedSnapIndex(emblaApi);

  useAutoplayOnInView(emblaApi, inView);

  if (list.length === 0 || !isDesktop) return null;

  return (
    <section
      ref={inViewRef}
      className={cn(
        'relative flex w-full flex-col items-center overflow-hidden',
        className,
      )}
    >
      <div
        className={cn('w-full pb-60 pt-16 transition-opacity duration-300', {
          'opacity-0': !isSwiperReady,
          'opacity-100': isSwiperReady,
        })}
        ref={emblaRef}
      >
        <div className="flex">
          {list.map(
            (
              {
                donationBusinessId,
                donationBusinessSlugId,
                institutionLogoUrl,
                institutionName,
                thumbnailUrl,
                achievementRate,
                donationAmount,
                donationBusinessName,
                dday,
                specificDonationStatus,
              },
              index,
            ) => {
              const step = Math.abs(index - activeIndex);
              const distanceFromActive = Math.min(step, list.length - step);
              const isActiveIndex = distanceFromActive === 0;
              const isBlur = distanceFromActive === 1;
              const isHide = distanceFromActive > 1;

              return (
                <div
                  className={cn('relative z-0 flex-[0_0_auto] px-12', {
                    'z-10': !isBlur,
                  })}
                  key={donationBusinessId}
                  onClick={() => emblaApi?.plugins().autoplay?.stop()}
                >
                  <CustomLink
                    prefetch={false}
                    href={DONATION_ROUTES.HOMETOWN_BUSINESS_DETAIL.pathname({
                      id: donationBusinessSlugId ?? donationBusinessId,
                    })}
                    aria-label={`'${donationBusinessName}' 지정기부사업 상세 페이지로 이동`}
                    className={cn(
                      'relative flex w-[75rem] max-w-full flex-col gap-40 overflow-hidden rounded-[40px] border-x border-t border-Gray-black/5 p-40',
                      'opacity-100 transition-[opacity,box-shadow] duration-300 ease-in-out',
                      {
                        'shadow-[0_20px_19px_0_rgba(0,0,0,0.06),0_0_8px_0_rgba(0,0,0,0.03)]':
                          isActiveIndex,
                        'blur-sm': isBlur,
                        'opacity-0': isHide,
                      },
                    )}
                  >
                    {/* 배경 이미지 - 데스크톱에서만 로드 */}
                    <Image
                      src={thumbnailUrl}
                      alt={`${donationBusinessName} 썸네일 이미지`}
                      fill
                      className="-z-10 object-cover"
                      sizes={`(max-width: ${BREAKPOINTS.MOBILE_TABLET}) 0px, 1198px`}
                    />

                    {/* 그라디언트 오버레이 */}
                    <div
                      className="absolute inset-0 z-0"
                      style={{
                        background:
                          'linear-gradient(270deg, rgba(0,0,0,0.56) 0%, rgba(0,0,0,0.00) 35%, rgba(0,0,0,0.56) 100%)',
                      }}
                    />
                    <p className="relative z-10 line-clamp-2 h-68 max-w-[24.3125rem] font-700 text-Gray-white typo-h4">
                      현재 진행중인 고향 사업을 살펴보고 따뜻한 손길을 내밀어요.
                    </p>

                    <div className="relative z-10 flex w-full flex-col">
                      <div className="flex items-center justify-between pb-12">
                        <div className="flex items-center gap-4">
                          <div className="relative size-24 overflow-hidden rounded-pill border border-Gray-300">
                            <Image
                              fill
                              src={institutionLogoUrl}
                              alt={`${institutionName} 로고 이미지`}
                              className="object-cover"
                              sizes="24px"
                            />
                          </div>
                          <p className="font-600 text-Gray-200 typo-body4">
                            {institutionName}
                          </p>
                        </div>

                        <p className="font-600 text-Gray-200 typo-body3">
                          {isDonationBusinessEnded({
                            specificDonationStatus,
                            dday,
                          })
                            ? '마감'
                            : dday === 0
                              ? '오늘 마감'
                              : `${dday}일 남음`}
                        </p>
                      </div>

                      <p className="pb-32 font-600 text-Gray-white typo-h6">
                        {donationBusinessName}
                      </p>

                      <div className="relative mb-16 h-10 w-full rounded-pill bg-Gray-white/60">
                        <div
                          className="h-full rounded-pill bg-B-500"
                          style={{
                            width: `${achievementRate > 100 ? 100 : achievementRate}%`,
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <p className="font-700 text-B-500 typo-h6">
                          {achievementRate}%
                        </p>
                        <p className="font-700 text-Gray-white typo-h6">
                          {donationAmount.toLocaleString()} 원
                        </p>
                      </div>
                    </div>

                    <div
                      className={cn(
                        'absolute inset-0 size-full bg-Gray-black/60 opacity-0 transition-opacity duration-300',
                        {
                          'opacity-100': isBlur,
                        },
                      )}
                    />
                  </CustomLink>
                </div>
              );
            },
          )}
        </div>
      </div>
      <Button theme="sub-fill" dimensions="H40" asChild>
        <CustomLink
          prefetch={false}
          href={DONATION_ROUTES.HOMETOWNS_BUSINESS.pathname}
          aria-label="전체 지정기부사업 목록 페이지로 이동"
          className="group flex w-[30rem] max-w-full items-center gap-4"
        >
          지정기부사업 전체보기
          <SvgChevronright className="size-16 text-Gray-black transition-transform duration-150 group-hover:translate-x-1 group-focus-visible:translate-x-1 motion-reduce:transition-none" />
        </CustomLink>
      </Button>
    </section>
  );
};
